package co.laiautomation.downtyme

import com.laiautomation.kdxp.repositories.KdxpObjectDefinitionRecord
import com.laiautomation.kdxp.repositories.KdxpObjectRepository
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonObject

/** Stable Liferay Object identifiers owned by Downtyme. */
object DowntymeObjectKeys {
    const val CHARACTER_ERC = "DOWNTYME_CHARACTER"
    const val AREA_ERC = "DOWNTYME_AREA"
    const val ACTION_ERC = "DOWNTYME_ACTION"
    const val RESOURCE_ERC = "DOWNTYME_RESOURCE_TYPE"
    const val ACTION_LOG_ERC = "DOWNTYME_ACTION_LOG"

    const val CHARACTER = "DowntymeCharacter"
    const val AREA = "DowntymeArea"
    const val ACTION = "DowntymeAction"
    const val RESOURCE = "DowntymeResourceType"
    const val ACTION_LOG = "DowntymeActionLog"

    const val CHARACTER_REST_CONTEXT = "downtymecharacters"
    const val AREA_REST_CONTEXT = "downtymeareas"
    const val ACTION_REST_CONTEXT = "downtymeactions"
    const val RESOURCE_REST_CONTEXT = "downtymeresourcetypes"
    const val ACTION_LOG_REST_CONTEXT = "downtymeactionlogs"

    const val AREA_CHARACTERS = "downtymeAreaCharacters"
    const val AREA_ACTIONS = "downtymeAreaActions"
    const val RESOURCE_ACTIONS = "downtymeResourceActions"
    const val CHARACTER_LOGS = "downtymeCharacterActionLogs"
    const val ACTION_LOGS = "downtymeActionLogs"
    const val RESOURCE_LOGS = "downtymeResourceActionLogs"
}

data class DowntymeSchemaBinding(
    val definitionId: Long?,
    val externalReferenceCode: String,
    val name: String,
    val restContextPath: String,
)

data class DowntymeSchemaBindings(
    val characters: DowntymeSchemaBinding,
    val areas: DowntymeSchemaBinding,
    val actions: DowntymeSchemaBinding,
    val resources: DowntymeSchemaBinding,
    val actionLogs: DowntymeSchemaBinding,
) {
    companion object {
        /** Runtime bindings avoid requiring Object-definition administration on ordinary app launches. */
        internal fun runtimeDefaults(): DowntymeSchemaBindings = DowntymeSchemaBindings(
            characters = runtimeBinding(
                externalReferenceCode = DowntymeObjectKeys.CHARACTER_ERC,
                name = DowntymeObjectKeys.CHARACTER,
                restContextPath = DowntymeObjectKeys.CHARACTER_REST_CONTEXT,
            ),
            areas = runtimeBinding(
                externalReferenceCode = DowntymeObjectKeys.AREA_ERC,
                name = DowntymeObjectKeys.AREA,
                restContextPath = DowntymeObjectKeys.AREA_REST_CONTEXT,
            ),
            actions = runtimeBinding(
                externalReferenceCode = DowntymeObjectKeys.ACTION_ERC,
                name = DowntymeObjectKeys.ACTION,
                restContextPath = DowntymeObjectKeys.ACTION_REST_CONTEXT,
            ),
            resources = runtimeBinding(
                externalReferenceCode = DowntymeObjectKeys.RESOURCE_ERC,
                name = DowntymeObjectKeys.RESOURCE,
                restContextPath = DowntymeObjectKeys.RESOURCE_REST_CONTEXT,
            ),
            actionLogs = runtimeBinding(
                externalReferenceCode = DowntymeObjectKeys.ACTION_LOG_ERC,
                name = DowntymeObjectKeys.ACTION_LOG,
                restContextPath = DowntymeObjectKeys.ACTION_LOG_REST_CONTEXT,
            ),
        )

        private fun runtimeBinding(
            externalReferenceCode: String,
            name: String,
            restContextPath: String,
        ): DowntymeSchemaBinding = DowntymeSchemaBinding(
            definitionId = null,
            externalReferenceCode = externalReferenceCode,
            name = name,
            restContextPath = restContextPath,
        )
    }
}

/**
 * Installs or repairs Downtyme's site-scoped Liferay Object schema.
 *
 * Provisioning is idempotent: existing definitions, fields, and relationships
 * are reused by stable names and external reference codes.
 */
class DowntymeSchemaProvisioner(
    private val objects: KdxpObjectRepository,
) {
    suspend fun provision(): DowntymeSchemaBindings {
        objects.ensureConfiguredObjectFolder()
        val knownDefinitions = objects.listAllDefinitions().toMutableList()
        val ensuredDefinitions = ALL_SCHEMAS.map { schema ->
            ensureDefinition(schema, knownDefinitions)
        }

        ensuredDefinitions
            .filter { ensured -> ensured.fieldsChanged || ensured.record.active != true }
            .forEach { ensured -> objects.publishDefinition(ensured.record.id) }

        // Relationships are created against published definitions so Liferay
        // has generated the backing tables and REST contexts first.
        val publishedDefinitions = objects.listAllDefinitions()
        val areas = publishedDefinitions.requireDefinition(AREA_SCHEMA)
        val resources = publishedDefinitions.requireDefinition(RESOURCE_SCHEMA)
        val characters = publishedDefinitions.requireDefinition(CHARACTER_SCHEMA)
        val actions = publishedDefinitions.requireDefinition(ACTION_SCHEMA)
        val logs = publishedDefinitions.requireDefinition(ACTION_LOG_SCHEMA)

        ensureRelationship(areas, characters, DowntymeObjectKeys.AREA_CHARACTERS, "Area Characters")
        ensureRelationship(areas, actions, DowntymeObjectKeys.AREA_ACTIONS, "Area Actions")
        ensureRelationship(resources, actions, DowntymeObjectKeys.RESOURCE_ACTIONS, "Resource Actions")
        ensureRelationship(characters, logs, DowntymeObjectKeys.CHARACTER_LOGS, "Character Action Logs")
        ensureRelationship(actions, logs, DowntymeObjectKeys.ACTION_LOGS, "Action Logs")
        ensureRelationship(resources, logs, DowntymeObjectKeys.RESOURCE_LOGS, "Resource Action Logs")

        return objects.listAllDefinitions().toBindings()
    }

    suspend fun resolveBindings(): DowntymeSchemaBindings =
        objects.listAllDefinitions().toBindings()

    private suspend fun ensureDefinition(
        schema: ObjectSchema,
        knownDefinitions: MutableList<KdxpObjectDefinitionRecord>,
    ): EnsuredDefinition {
        val existing = knownDefinitions.firstOrNull { definition ->
            definition.matches(schema)
        }
        val definition = existing ?: objects.createDefinition(
            buildJsonObject {
                put("externalReferenceCode", schema.externalReferenceCode)
                putJsonObject("label") { put(DEFAULT_LOCALE, schema.label) }
                put("name", schema.name)
                putJsonObject("pluralLabel") { put(DEFAULT_LOCALE, schema.pluralLabel) }
                put("scope", "site")
            },
        ).also(knownDefinitions::add)
        val folderedDefinition = objects.ensureDefinitionFolder(definition.id)

        val existingFields = objects.listAllFields(folderedDefinition.id)
            .mapNotNull { it.name }
            .map(String::lowercase)
            .toSet()
        var fieldsChanged = false
        schema.fields.filterNot { it.name.lowercase() in existingFields }.forEach { field ->
            objects.createFieldForSuccess(
                folderedDefinition.id,
                buildJsonObject {
                    put("businessType", field.businessType)
                    putJsonObject("label") { put(DEFAULT_LOCALE, field.label) }
                    put("name", field.name)
                    put("required", field.required)
                },
            )
            fieldsChanged = true
        }
        return EnsuredDefinition(folderedDefinition, fieldsChanged)
    }

    private suspend fun ensureRelationship(
        one: KdxpObjectDefinitionRecord,
        many: KdxpObjectDefinitionRecord,
        name: String,
        label: String,
    ) {
        val exists = objects.listAllRelationships(one.id).any {
            it.name.equals(name, ignoreCase = true)
        }
        if (exists) return

        objects.createRelationship(
            one.id,
            buildJsonObject {
                put("deletionType", "disassociate")
                putJsonObject("label") { put(DEFAULT_LOCALE, label) }
                put("name", name)
                put("objectDefinitionId1", one.id)
                put("objectDefinitionId2", many.id)
                put("type", "oneToMany")
            },
        )
    }

    private data class EnsuredDefinition(
        val record: KdxpObjectDefinitionRecord,
        val fieldsChanged: Boolean,
    )

    private data class ObjectSchema(
        val externalReferenceCode: String,
        val name: String,
        val label: String,
        val pluralLabel: String,
        val fields: List<Field>,
    )

    private data class Field(
        val name: String,
        val label: String,
        val businessType: String,
        val required: Boolean = false,
    )

    private companion object {
        const val DEFAULT_LOCALE = "en_US"

        val AREA_SCHEMA = ObjectSchema(
            DowntymeObjectKeys.AREA_ERC,
            DowntymeObjectKeys.AREA,
            "Area",
            "Areas",
            listOf(
                Field("name", "Name", "Text", true),
                Field("description", "Description", "LongText"),
                Field("imageUrl", "Image URL", "Text"),
            ),
        )
        val RESOURCE_SCHEMA = ObjectSchema(
            DowntymeObjectKeys.RESOURCE_ERC,
            DowntymeObjectKeys.RESOURCE,
            "Currency or Resource",
            "Currencies and Resources",
            listOf(
                Field("name", "Name", "Text", true),
                Field("description", "Description", "LongText"),
                Field("iconKey", "Icon Key", "Text"),
            ),
        )
        val CHARACTER_SCHEMA = ObjectSchema(
            DowntymeObjectKeys.CHARACTER_ERC,
            DowntymeObjectKeys.CHARACTER,
            "Character",
            "Characters",
            listOf(
                Field("name", "Name", "Text", true),
                Field("description", "Description", "LongText"),
                Field("imageUrl", "Image URL", "Text"),
                Field("controllingUserId", "Controlling User ID", "LongInteger"),
                Field("capital", "Capital", "Integer"),
            ),
        )
        val ACTION_SCHEMA = ObjectSchema(
            DowntymeObjectKeys.ACTION_ERC,
            DowntymeObjectKeys.ACTION,
            "Downtime Action",
            "Downtime Actions",
            listOf(
                Field("name", "Name", "Text", true),
                Field("description", "Description", "LongText"),
                Field("imageUrl", "Image URL", "Text"),
                Field("cost", "Cost", "Integer"),
            ),
        )
        val ACTION_LOG_SCHEMA = ObjectSchema(
            DowntymeObjectKeys.ACTION_LOG_ERC,
            DowntymeObjectKeys.ACTION_LOG,
            "Action Log",
            "Action Logs",
            listOf(
                Field("submittedByUserId", "Submitted By User ID", "LongInteger", true),
                Field("amountSpent", "Amount Spent", "Integer"),
                Field("notes", "Notes", "LongText"),
                Field("createdAtEpochMillis", "Created At Epoch Millis", "LongInteger", true),
            ),
        )

        val ALL_SCHEMAS = listOf(
            AREA_SCHEMA,
            RESOURCE_SCHEMA,
            CHARACTER_SCHEMA,
            ACTION_SCHEMA,
            ACTION_LOG_SCHEMA,
        )
    }

    private fun KdxpObjectDefinitionRecord.matches(schema: ObjectSchema): Boolean =
        externalReferenceCode.equals(schema.externalReferenceCode, ignoreCase = true) ||
            name.equals(schema.name, ignoreCase = true)

    private fun List<KdxpObjectDefinitionRecord>.requireDefinition(
        schema: ObjectSchema,
    ): KdxpObjectDefinitionRecord = firstOrNull { it.matches(schema) }
        ?: error("Downtyme object definition ${schema.externalReferenceCode} is not installed.")

    private fun List<KdxpObjectDefinitionRecord>.toBindings(): DowntymeSchemaBindings =
        DowntymeSchemaBindings(
            characters = requireDefinition(CHARACTER_SCHEMA).toBinding(CHARACTER_SCHEMA),
            areas = requireDefinition(AREA_SCHEMA).toBinding(AREA_SCHEMA),
            actions = requireDefinition(ACTION_SCHEMA).toBinding(ACTION_SCHEMA),
            resources = requireDefinition(RESOURCE_SCHEMA).toBinding(RESOURCE_SCHEMA),
            actionLogs = requireDefinition(ACTION_LOG_SCHEMA).toBinding(ACTION_LOG_SCHEMA),
        )

    private fun KdxpObjectDefinitionRecord.toBinding(
        schema: ObjectSchema,
    ): DowntymeSchemaBinding = DowntymeSchemaBinding(
        definitionId = id,
        externalReferenceCode = externalReferenceCode
            ?.takeIf(String::isNotBlank)
            ?: schema.externalReferenceCode,
        name = name ?: schema.name,
        restContextPath = restContextPath
            ?: error("${schema.externalReferenceCode} is missing its REST context path; publish the definition."),
    )
}
